(function () {
  'use strict';

  var _reactJsxRuntime = {
    jsx: function(type, props) {
      var children = props ? props.children : undefined;
      var rest = {};
      if (props) { for (var k in props) { if (k !== 'children') rest[k] = props[k]; } }
      if (Array.isArray(children)) {
        return React.createElement.apply(React, [type, rest].concat(children));
      } else if (children !== undefined) {
        return React.createElement(type, rest, children);
      } else {
        return React.createElement(type, rest);
      }
    },
    jsxs: function(type, props) {
      return _reactJsxRuntime.jsx(type, props);
    }
  };

const { useState, useMemo, useEffect } = React;
const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } = Recharts;

const RAILWRAP_LENGTH_M = 10.5;
const REGIONS = {
  AU: {
    label: 'Australia (AUD)', symbol: 'A$',
    labourMin: 35, labourMax: 120, labourDefault: 55,
    scCostPerSqM: 2.50, scHint: 'Heavy-duty scaffolding mesh ~$2.00-$3.00/m2',
    disposalPerSqM: 0.60,
    rwPricePer10_5m: { 1.0: 291.50, 1.5: 346.50, 1.9: 621.50 },
  },
  USA: {
    label: 'United States (USD)', symbol: '$',
    labourMin: 17, labourMax: 80, labourDefault: 24,
    scCostPerSqM: 5.38, scHint: 'Industrial shade cloth ~$0.33-$0.70/sq ft',
    disposalPerSqM: 0.75,
    rwCostPerMeter: { 1.0: 32, 1.5: 44, 1.9: 54 },
  }
};

const HEIGHTS = [
  { label: '1.0 m (39 in)', value: 1.0, sku: 'H06038-1' },
  { label: '1.5 m (59 in)', value: 1.5, sku: 'H06038-1.5' },
  { label: '1.9 m (75 in)', value: 1.9, sku: 'H06038-1.9' },
];

const SC_MAINTENANCE_FACTOR = 1.10;
const INSTALL_TIME_RW_MINS = 5;
const INSTALL_TIME_SC_MINS = 12;

function fmt(val, symbol) { return symbol + Math.round(val).toLocaleString(); }

function Slider(props) {
  return _reactJsxRuntime.jsxs("div", { style: { marginBottom: '18px' }, children: [
    _reactJsxRuntime.jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }, children: [
      _reactJsxRuntime.jsx("label", { style: { fontWeight: '600', fontSize: '12px', color: '#222' }, children: props.label }),
      _reactJsxRuntime.jsx("span", { style: { fontWeight: '700', fontSize: '15px', color: '#fff', background: '#E1251B', padding: '2px 10px', borderRadius: '4px' }, children: (props.unit === 'A$' || props.unit === '$') ? props.unit + props.value.toLocaleString() : props.value + ' ' + props.unit })
    ]}),
    _reactJsxRuntime.jsx("div", { style: { position: 'relative', height: '20px', display: 'flex', alignItems: 'center' }, children:
      _reactJsxRuntime.jsx("input", { type: "range", min: props.min, max: props.max, step: props.step, value: props.value, onChange: function(e) { props.onChange(Number(e.target.value)); }, style: { width: '100%', accentColor: '#E1251B', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', height: '3px', background: '#ddd', borderRadius: '2px', outline: 'none' } })
    }),
    _reactJsxRuntime.jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#aaa', marginTop: '2px' }, children: [
      _reactJsxRuntime.jsx("span", { children: props.min }),
      _reactJsxRuntime.jsx("span", { style: { fontStyle: 'italic', color: '#999' }, children: props.hint }),
      _reactJsxRuntime.jsx("span", { children: props.max })
    ]})
  ]});
}

function StatCard(props) {
  var bg = props.positive ? '#1a7a3a' : (props.highlight ? '#E1251B' : '#19191A');
  return _reactJsxRuntime.jsxs("div", { style: { background: bg, color: '#fff', borderRadius: '8px', padding: props.isMobile ? '10px 8px' : '12px 14px', textAlign: 'center', flex: '1', minWidth: props.isMobile ? '90px' : '110px' }, children: [
    _reactJsxRuntime.jsx("div", { style: { fontSize: props.isMobile ? '8px' : '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.75, marginBottom: '3px' }, children: props.label }),
    _reactJsxRuntime.jsx("div", { style: { fontSize: props.isMobile ? '16px' : '20px', fontWeight: '800', lineHeight: 1.1 }, children: props.value }),
    props.sub ? _reactJsxRuntime.jsx("div", { style: { fontSize: props.isMobile ? '8px' : '10px', marginTop: '3px', opacity: 0.65 }, children: props.sub }) : null
  ]});
}

function App() {
  const [region, setRegion] = useState('AU');
  const [height, setHeight] = useState(1.0);
  const [meters, setMeters] = useState(50);
  const [projectsPerYear, setProjectsPerYear] = useState(6);
  const [years, setYears] = useState(2);
  const [labourRate, setLabourRate] = useState(55);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(function() {
    var checkMobile = function() { setIsMobile(window.innerWidth < 768); };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return function() { window.removeEventListener('resize', checkMobile); };
  }, []);

  const R = REGIONS[region];
  const sym = R.symbol;

  const calc = useMemo(function() {
    const totalProjects = projectsPerYear * years;
    const sqmPerProject = meters * height;
    const scBaseMaterial = R.scCostPerSqM * sqmPerProject * totalProjects;
    const scMaterial = scBaseMaterial * SC_MAINTENANCE_FACTOR;
    const scInstallMins = meters * INSTALL_TIME_SC_MINS * totalProjects;
    const scLabour = (scInstallMins / 60) * labourRate;
    const scDisposal = R.disposalPerSqM * sqmPerProject * totalProjects;
    const scTotal = scMaterial + scLabour + scDisposal;

    let rwPackPrice = region === 'AU' ? (R.rwPricePer10_5m[height] || 0) : 0;
    const packsNeeded = Math.ceil(meters / RAILWRAP_LENGTH_M);
    const rwPurchase = region === 'AU' ? packsNeeded * rwPackPrice : meters * (R.rwCostPerMeter[height] || 0);
    const rwLabour = (meters * INSTALL_TIME_RW_MINS * totalProjects / 60) * labourRate;
    const rwTotal = rwPurchase + rwLabour;

    const totalSavings = scTotal - rwTotal;
    const chartData = [];
    for (let p = 1; p <= Math.min(totalProjects, 24); p++) {
      const scCum = ((R.scCostPerSqM * sqmPerProject * p) * SC_MAINTENANCE_FACTOR) + ((meters * INSTALL_TIME_SC_MINS * p / 60) * labourRate) + (R.disposalPerSqM * sqmPerProject * p);
      const rwCum = rwPurchase + ((meters * INSTALL_TIME_RW_MINS * p / 60) * labourRate);
      chartData.push({ project: 'P' + p, 'Shade Cloth': Math.round(scCum), 'Rail Wrap': Math.round(rwCum) });
    }

    return {
      totalSavings, scTotal, rwTotal, chartData, packsNeeded, rwPackPrice,
      rwUnitCostPerM: region === 'AU' ? rwPackPrice / RAILWRAP_LENGTH_M : (R.rwCostPerMeter[height] || 0),
      totalLabourSaved: ((meters * (INSTALL_TIME_SC_MINS - INSTALL_TIME_RW_MINS) * totalProjects) / 60).toFixed(0),
      totalWasteAvoided: (sqmPerProject * totalProjects).toFixed(0),
      breakEvenProject: Math.ceil(rwPurchase / ((scTotal - rwLabour) / totalProjects))
    };
  }, [region, height, meters, projectsPerYear, years, labourRate, R]);

  return _reactJsxRuntime.jsxs("div", { style: { fontFamily: 'Arial, sans-serif', background: '#f0f0f0', minHeight: 'auto', paddingBottom: '40px' }, children: [

    _reactJsxRuntime.jsxs("div", { style: { maxWidth: '980px', margin: '0 auto', padding: isMobile ? '12px 10px' : '20px 16px' }, children: [
      _reactJsxRuntime.jsx("div", { style: { background: '#fff', borderRadius: '8px', padding: isMobile ? '12px' : '14px 18px', marginBottom: '16px', borderLeft: '4px solid #E1251B' }, children: 
        _reactJsxRuntime.jsxs("p", { style: { margin: 0, fontSize: isMobile ? '11px' : '12px', color: '#444', lineHeight: 1.6 }, children: [
          _reactJsxRuntime.jsx("strong", { children: "Total Cost of Ownership (TCO):" }), " compares single-use shade cloth vs. reusable Rail Wrap including material, labour and disposal. ",
          _reactJsxRuntime.jsx("em", { children: "Planning horizon has a 2-year minimum to reflect typical industrial lifecycle decisions." })
        ]})
      }),
      _reactJsxRuntime.jsxs("div", { style: { display: 'flex', gap: '16px', flexWrap: 'wrap' }, children: [
        _reactJsxRuntime.jsxs("div", { style: { flex: '1', minWidth: isMobile ? '100%' : '260px', background: '#fff', borderRadius: '8px', padding: isMobile ? '14px' : '18px' }, children: [
          _reactJsxRuntime.jsx("h3", { style: { margin: '0 0 14px 0', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }, children: "Site Parameters" }),
          _reactJsxRuntime.jsxs("div", { style: { marginBottom: '18px' }, children: [
            _reactJsxRuntime.jsx("div", { style: { fontWeight: '600', fontSize: '12px', color: '#222', marginBottom: '6px' }, children: "Rail Wrap Height" }),
            _reactJsxRuntime.jsx("div", { style: { display: 'flex', gap: '8px' }, children: HEIGHTS.map(h => (
              _reactJsxRuntime.jsxs("button", { key: h.value, onClick: () => setHeight(h.value), style: { flex: 1, padding: '8px 4px 6px', borderRadius: '5px', border: '2px solid', cursor: 'pointer', fontWeight: '700', fontSize: isMobile ? '10px' : '11px', lineHeight: 1.3, textAlign: 'center', borderColor: height === h.value ? '#19191A' : '#ddd', background: height === h.value ? '#19191A' : '#f5f5f5', color: height === h.value ? '#fff' : '#555' }, children: [
                _reactJsxRuntime.jsx("div", { children: h.label }),
                _reactJsxRuntime.jsx("div", { style: { fontWeight: '400', fontSize: '9px', color: height === h.value ? 'rgba(255,255,255,0.5)' : '#bbb', marginTop: '3px', letterSpacing: '0.3px' }, children: h.sku })
              ]})
            ))})
          ]}),
          _reactJsxRuntime.jsx(Slider, { label: "Linear Meters", value: meters, min: 10, max: 200, step: 10, onChange: setMeters, unit: "m", hint: "Total edge length" }),
          _reactJsxRuntime.jsx(Slider, { label: "Projects Per Year", value: projectsPerYear, min: 1, max: 12, step: 1, onChange: setProjectsPerYear, unit: "projects", hint: "Replacement frequency" }),
          _reactJsxRuntime.jsx(Slider, { label: "Planning Horizon", value: years, min: 2, max: 5, step: 1, onChange: setYears, unit: "years", hint: "Minimum 2 years" }),
          _reactJsxRuntime.jsx(Slider, { label: "Labour Rate", value: labourRate, min: 35, max: 120, step: 1, onChange: setLabourRate, unit: sym, hint: "Average Hourly Rate" }),
          _reactJsxRuntime.jsxs("div", { style: { background: '#f9f9f9', borderRadius: '6px', padding: '10px 12px', marginTop: '4px', fontSize: '10px', color: '#666', lineHeight: 1.8 }, children: [
            _reactJsxRuntime.jsxs("strong", { style: { color: '#333', fontSize: '11px' }, children: ["Assumptions (", region, "):"] }), _reactJsxRuntime.jsx("br", {}),
            "Shade cloth: ", sym, R.scCostPerSqM.toFixed(2), "/m2 · 10% maintenance · ", sym, R.disposalPerSqM.toFixed(2), "/m2 disposal · 12 min/m install", _reactJsxRuntime.jsx("br", {}),
            _reactJsxRuntime.jsx("span", { style: { color: '#aaa', fontSize: '9px' }, children: R.scHint }), _reactJsxRuntime.jsx("br", {}),
            "Rail Wrap: ", sym, calc.rwPackPrice.toFixed(2), " per 10.5m length · ", sym, calc.rwUnitCostPerM.toFixed(2), "/m · 5 min/m install *", _reactJsxRuntime.jsx("br", {}),
            "Purchase rounding: packs needed = ", calc.packsNeeded
          ]})
        ]}),
        _reactJsxRuntime.jsxs("div", { style: { flex: '1.3', minWidth: isMobile ? '100%' : '300px', display: 'flex', flexDirection: 'column', gap: '14px' }, children: [
          _reactJsxRuntime.jsxs("div", { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' }, children: [
            _reactJsxRuntime.jsx(StatCard, { label: calc.totalSavings > 0 ? "Total Savings" : "Break-Even At", value: calc.totalSavings > 0 ? fmt(calc.totalSavings, sym) : "Project " + calc.breakEvenProject, positive: calc.totalSavings > 0, isMobile: isMobile }),
            _reactJsxRuntime.jsx(StatCard, { label: "Labour Saved", value: calc.totalLabourSaved + " hrs", isMobile: isMobile }),
            _reactJsxRuntime.jsx(StatCard, { label: "Waste Avoided", value: calc.totalWasteAvoided + " m2", isMobile: isMobile })
          ]}),
          _reactJsxRuntime.jsxs("div", { style: { background: calc.totalSavings > 0 ? '#1a7a3a' : '#19191A', borderRadius: '8px', padding: isMobile ? '12px' : '14px 18px', textAlign: 'center' }, children: [
            _reactJsxRuntime.jsx("div", { style: { color: 'rgba(255,255,255,0.7)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }, children: "Estimated Lifecycle Saving" }),
            _reactJsxRuntime.jsx("div", { style: { color: '#fff', fontSize: isMobile ? '28px' : '32px', fontWeight: '900' }, children: fmt(calc.totalSavings, sym) }),
            _reactJsxRuntime.jsx("div", { style: { color: 'rgba(255,255,255,0.75)', fontSize: '11px' }, children: "Based on a " + years + "-year planning horizon" })
          ]}),
          _reactJsxRuntime.jsxs("div", { style: { background: '#fff', borderRadius: '8px', padding: isMobile ? '14px' : '18px' }, children: [
            _reactJsxRuntime.jsx("h3", { style: { margin: '0 0 4px 0', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }, children: "Cumulative Cost Per Project" }),
            _reactJsxRuntime.jsxs(BarChart, { width: isMobile ? 300 : 500, height: 300, data: calc.chartData || [], margin: { top: 10, right: 10, left: 0, bottom: 0 }, children: [
              _reactJsxRuntime.jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "#f0f0f0" }),
              _reactJsxRuntime.jsx(XAxis, { dataKey: "project", tick: { fontSize: 9 } }),
              _reactJsxRuntime.jsx(YAxis, { tick: { fontSize: 9 } }),
              _reactJsxRuntime.jsx(Tooltip, {}),
              _reactJsxRuntime.jsx(Legend, { wrapperStyle: { fontSize: '11px' } }),
              _reactJsxRuntime.jsx(Bar, { dataKey: "Shade Cloth", fill: "#E1251B", radius: [3, 3, 0, 0] }),
              _reactJsxRuntime.jsx(Bar, { dataKey: "Rail Wrap", fill: "#19191A", radius: [3, 3, 0, 0] })
            ]})
          ]})
        ]})
      ]})
    ]})
  ]});
}

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.getElementById('gripps-railwrap-calculator');
    if (root && typeof ReactDOM !== 'undefined') {
      ReactDOM.render(React.createElement(App), root);
    }
  });
})();